import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import {
  ArrowLeft,
  TextT,
  Highlighter,
  PencilSimple,
  Eraser,
  FloppyDisk,
  Trash,
  MagnifyingGlassPlus,
  MagnifyingGlassMinus,
  CaretLeft,
  CaretRight,
  X,
  Check,
  Circle,
  Eye,
  EyeSlash
} from '@phosphor-icons/react';
import * as pdfjsLib from 'pdfjs-dist';

// Set PDF.js worker - using legacy build for better compatibility with CRA
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tool types
const TOOLS = {
  SELECT: 'select',
  TEXT: 'text',
  HIGHLIGHT: 'highlight',
  FREEHAND: 'freehand',
  ERASER: 'eraser'
};

// Default colors for tools (reversed order, blue first for text)
const COLORS = [
  '#000000', // Black
  '#DDA0DD', // Plum
  '#FFEAA7', // Light yellow
  '#96CEB4', // Green
  '#45B7D1', // Blue (default for text)
  '#4ECDC4', // Teal
  '#FF6B6B', // Red
  '#FFFF00', // Yellow (default highlight)
];

// Default color for text annotations
const DEFAULT_TEXT_COLOR = '#45B7D1'; // Blue
const DEFAULT_HIGHLIGHT_COLOR = '#FFFF00'; // Yellow

export const MetaNotePage = () => {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const { t } = useLanguage();
  
  // Document state
  const [document, setDocument] = useState(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [scale, setScale] = useState(1.0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  
  // Annotation state
  const [annotations, setAnnotations] = useState({});
  const [currentTool, setCurrentTool] = useState(TOOLS.SELECT);
  const [currentColor, setCurrentColor] = useState(DEFAULT_TEXT_COLOR);
  const [strokeWidth, setStrokeWidth] = useState(3);
  const [fontSize, setFontSize] = useState(16);
  const [opacity, setOpacity] = useState(0.5);
  const [showAnnotations, setShowAnnotations] = useState(true);
  
  // Drawing state
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentPath, setCurrentPath] = useState([]);
  const [selectedAnnotation, setSelectedAnnotation] = useState(null);
  const [textInput, setTextInput] = useState({ visible: false, x: 0, y: 0, text: '', editingId: null });
  
  // Refs
  const canvasRef = useRef(null);
  const annotationCanvasRef = useRef(null);
  const containerRef = useRef(null);
  
  // Track unsaved changes
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Fetch document info
  useEffect(() => {
    const fetchDocument = async () => {
      try {
        const response = await fetch(`${API_URL}/api/documents/${documentId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setDocument(data);
        }
      } catch (error) {
        console.error('Error fetching document:', error);
      }
    };
    
    if (documentId && token) {
      fetchDocument();
    }
  }, [documentId, token]);
  
  // Load PDF
  useEffect(() => {
    const loadPDF = async () => {
      if (!document || !token) return;
      
      try {
        setLoading(true);
        
        // Determine which file to load (PDF/A if available, otherwise original if PDF)
        let pdfUrl;
        if (document.has_pdfa) {
          pdfUrl = `${API_URL}/api/documents/${documentId}/preview-pdfa?token=${token}`;
        } else if (document.content_type === 'application/pdf' || document.original_filename?.toLowerCase().endsWith('.pdf')) {
          pdfUrl = `${API_URL}/api/documents/${documentId}/preview?token=${token}`;
        } else {
          // Try PDF/A conversion result
          pdfUrl = `${API_URL}/api/documents/${documentId}/preview-pdfa?token=${token}`;
        }
        
        const loadingTask = pdfjsLib.getDocument(pdfUrl);
        const pdf = await loadingTask.promise;
        
        setPdfDoc(pdf);
        setTotalPages(pdf.numPages);
        setCurrentPage(1);
      } catch (error) {
        console.error('Error loading PDF:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadPDF();
  }, [document, documentId, token]);
  
  // Load annotations
  useEffect(() => {
    const loadAnnotations = async () => {
      try {
        const response = await fetch(`${API_URL}/api/documents/${documentId}/annotations`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setAnnotations(data.annotations_by_page || {});
          if (data.last_updated) {
            setLastSaved(new Date(data.last_updated));
          }
        }
      } catch (error) {
        console.error('Error loading annotations:', error);
      }
    };
    
    if (documentId && token) {
      loadAnnotations();
    }
  }, [documentId, token]);
  
  // Render PDF page
  useEffect(() => {
    const renderPage = async () => {
      if (!pdfDoc || !canvasRef.current) return;
      
      try {
        const page = await pdfDoc.getPage(currentPage);
        const viewport = page.getViewport({ scale });
        
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');
        
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        
        // Also resize annotation canvas
        if (annotationCanvasRef.current) {
          annotationCanvasRef.current.width = viewport.width;
          annotationCanvasRef.current.height = viewport.height;
        }
        
        await page.render({
          canvasContext: context,
          viewport: viewport
        }).promise;
        
        // Render annotations after PDF
        renderAnnotations();
      } catch (error) {
        console.error('Error rendering page:', error);
      }
    };
    
    renderPage();
  }, [pdfDoc, currentPage, scale]);
  
  // Render annotations on canvas
  const renderAnnotations = useCallback(() => {
    if (!annotationCanvasRef.current) return;
    
    const canvas = annotationCanvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Skip rendering saved annotations if hidden
    if (!showAnnotations) {
      // Still render current drawing path
      if (isDrawing && currentPath.length > 0) {
        ctx.save();
        ctx.globalAlpha = currentTool === TOOLS.HIGHLIGHT ? opacity : 1;
        ctx.strokeStyle = currentColor;
        ctx.lineWidth = strokeWidth * scale;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        
        ctx.beginPath();
        ctx.moveTo(currentPath[0].x * scale, currentPath[0].y * scale);
        
        for (let i = 1; i < currentPath.length; i++) {
          ctx.lineTo(currentPath[i].x * scale, currentPath[i].y * scale);
        }
        
        ctx.stroke();
        ctx.restore();
      }
      return;
    }
    
    const pageAnnotations = annotations[currentPage] || [];
    
    pageAnnotations.forEach(ann => {
      const scaledX = ann.x * scale;
      const scaledY = ann.y * scale;
      
      ctx.save();
      
      if (ann.type === 'highlight') {
        ctx.globalAlpha = ann.style?.opacity || 0.5;
        ctx.fillStyle = ann.style?.color || '#FFFF00';
        ctx.fillRect(
          scaledX,
          scaledY,
          (ann.width || 100) * scale,
          (ann.height || 20) * scale
        );
      } else if (ann.type === 'text') {
        ctx.globalAlpha = 1;
        ctx.fillStyle = ann.style?.color || '#000000';
        const scaledFontSize = (ann.style?.fontSize || 14) * scale;
        ctx.font = `${scaledFontSize}px ${ann.style?.fontFamily || 'Arial'}`;
        
        // Handle multiline text
        const lines = (ann.content || '').split('\n');
        const lineHeight = scaledFontSize * 1.2;
        lines.forEach((line, index) => {
          ctx.fillText(line, scaledX, scaledY + (index * lineHeight));
        });
        
        // Update annotation dimensions for selection box
        ann._renderHeight = lines.length * lineHeight;
        ann._renderWidth = Math.max(...lines.map(line => ctx.measureText(line).width));
      } else if (ann.type === 'freehand' && ann.points && ann.points.length > 0) {
        ctx.globalAlpha = ann.style?.opacity || 1;
        ctx.strokeStyle = ann.style?.color || '#000000';
        ctx.lineWidth = (ann.style?.strokeWidth || 2) * scale;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        
        ctx.beginPath();
        ctx.moveTo(ann.points[0].x * scale, ann.points[0].y * scale);
        
        for (let i = 1; i < ann.points.length; i++) {
          const p = ann.points[i];
          ctx.lineTo(p.x * scale, p.y * scale);
        }
        
        ctx.stroke();
      }
      
      // Draw selection border if selected
      if (selectedAnnotation === ann.id) {
        ctx.strokeStyle = '#2196F3';
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 5]);
        
        if (ann.type === 'freehand' && ann.points) {
          // Draw bounding box for freehand
          const xs = ann.points.map(p => p.x * scale);
          const ys = ann.points.map(p => p.y * scale);
          const minX = Math.min(...xs) - 5;
          const minY = Math.min(...ys) - 5;
          const maxX = Math.max(...xs) + 5;
          const maxY = Math.max(...ys) + 5;
          ctx.strokeRect(minX, minY, maxX - minX, maxY - minY);
        } else {
          ctx.strokeRect(
            scaledX - 2,
            scaledY - (ann.type === 'text' ? (ann.style?.fontSize || 14) * scale : 2),
            ((ann.width || 100) + 4) * scale,
            ((ann.height || (ann.type === 'text' ? 20 : 20)) + 4) * scale
          );
        }
      }
      
      ctx.restore();
    });
    
    // Draw current path if drawing
    if (isDrawing && currentPath.length > 0) {
      ctx.save();
      ctx.globalAlpha = currentTool === TOOLS.HIGHLIGHT ? opacity : 1;
      ctx.strokeStyle = currentColor;
      ctx.lineWidth = strokeWidth * scale;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      
      ctx.beginPath();
      ctx.moveTo(currentPath[0].x * scale, currentPath[0].y * scale);
      
      for (let i = 1; i < currentPath.length; i++) {
        ctx.lineTo(currentPath[i].x * scale, currentPath[i].y * scale);
      }
      
      ctx.stroke();
      ctx.restore();
    }
  }, [annotations, currentPage, scale, selectedAnnotation, isDrawing, currentPath, currentTool, currentColor, strokeWidth, opacity, showAnnotations]);
  
  // Re-render annotations when they change
  useEffect(() => {
    renderAnnotations();
  }, [renderAnnotations]);
  
  // Save annotations to server (manual save only)
  const saveAnnotations = async () => {
    if (!documentId || !token) return;
    
    setSaving(true);
    try {
      // Flatten annotations from page-based structure
      const allAnnotations = [];
      Object.entries(annotations).forEach(([page, pageAnns]) => {
        pageAnns.forEach(ann => {
          allAnnotations.push({ ...ann, page: parseInt(page) });
        });
      });
      
      const response = await fetch(`${API_URL}/api/documents/${documentId}/annotations`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ annotations: allAnnotations })
      });
      
      if (response.ok) {
        const data = await response.json();
        setLastSaved(new Date(data.updated_at));
        setHasUnsavedChanges(false); // Reset unsaved changes flag
      }
    } catch (error) {
      console.error('Error saving annotations:', error);
    } finally {
      setSaving(false);
    }
  };
  
  // Get mouse position relative to canvas
  const getMousePos = (e) => {
    if (!annotationCanvasRef.current) return { x: 0, y: 0 };
    
    const rect = annotationCanvasRef.current.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) / scale,
      y: (e.clientY - rect.top) / scale
    };
  };
  
  // Handle mouse down
  const handleMouseDown = (e) => {
    const pos = getMousePos(e);
    
    if (currentTool === TOOLS.TEXT) {
      // Check if clicking on an existing text annotation to edit it
      const pageAnns = annotations[currentPage] || [];
      for (const ann of pageAnns) {
        if (ann.type === 'text' && isPointInAnnotation(pos, ann)) {
          // Open editor with existing text
          setTextInput({
            visible: true,
            x: ann.x,
            y: ann.y,
            text: ann.content || '',
            editingId: ann.id
          });
          // Set color and fontSize from annotation style
          if (ann.style?.color) setCurrentColor(ann.style.color);
          if (ann.style?.fontSize) setFontSize(ann.style.fontSize);
          setSelectedAnnotation(ann.id);
          return;
        }
      }
      
      // No existing annotation clicked - create new text at click position
      setTextInput({
        visible: true,
        x: pos.x,
        y: pos.y,
        text: '',
        editingId: null
      });
      return;
    }
    
    if (currentTool === TOOLS.ERASER) {
      // Check if clicking on an annotation to delete
      const pageAnns = annotations[currentPage] || [];
      for (const ann of pageAnns) {
        if (isPointInAnnotation(pos, ann)) {
          deleteAnnotation(ann.id);
          return;
        }
      }
      return;
    }
    
    if (currentTool === TOOLS.SELECT) {
      // Check if clicking on an annotation to select
      const pageAnns = annotations[currentPage] || [];
      let found = false;
      for (const ann of pageAnns) {
        if (isPointInAnnotation(pos, ann)) {
          // If clicking on already selected text annotation, open editor
          if (selectedAnnotation === ann.id && ann.type === 'text') {
            setTextInput({
              visible: true,
              x: ann.x,
              y: ann.y,
              text: ann.content || '',
              editingId: ann.id
            });
            // Set color and fontSize from annotation style
            if (ann.style?.color) setCurrentColor(ann.style.color);
            if (ann.style?.fontSize) setFontSize(ann.style.fontSize);
          }
          setSelectedAnnotation(ann.id);
          found = true;
          break;
        }
      }
      if (!found) {
        setSelectedAnnotation(null);
      }
      return;
    }
    
    if (currentTool === TOOLS.FREEHAND || currentTool === TOOLS.HIGHLIGHT) {
      setIsDrawing(true);
      setCurrentPath([{ x: pos.x, y: pos.y, pressure: 1 }]);
    }
  };
  
  // Handle mouse move
  const handleMouseMove = (e) => {
    if (!isDrawing) return;
    
    const pos = getMousePos(e);
    setCurrentPath(prev => [...prev, { x: pos.x, y: pos.y, pressure: 1 }]);
  };
  
  // Handle mouse up
  const handleMouseUp = () => {
    if (!isDrawing || currentPath.length < 2) {
      setIsDrawing(false);
      setCurrentPath([]);
      return;
    }
    
    // Create new annotation
    const newAnnotation = {
      id: `ann_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: currentTool === TOOLS.HIGHLIGHT ? 'highlight' : 'freehand',
      page: currentPage,
      x: Math.min(...currentPath.map(p => p.x)),
      y: Math.min(...currentPath.map(p => p.y)),
      width: Math.max(...currentPath.map(p => p.x)) - Math.min(...currentPath.map(p => p.x)),
      height: Math.max(...currentPath.map(p => p.y)) - Math.min(...currentPath.map(p => p.y)),
      points: currentTool === TOOLS.FREEHAND ? currentPath : null,
      style: {
        color: currentColor,
        opacity: currentTool === TOOLS.HIGHLIGHT ? opacity : 1,
        strokeWidth: strokeWidth
      },
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    
    // For highlight, create a rectangle instead of path
    if (currentTool === TOOLS.HIGHLIGHT) {
      newAnnotation.points = null;
    }
    
    addAnnotation(newAnnotation);
    
    setIsDrawing(false);
    setCurrentPath([]);
  };
  
  // Check if point is inside annotation
  const isPointInAnnotation = (point, ann) => {
    if (ann.type === 'freehand' && ann.points) {
      const xs = ann.points.map(p => p.x);
      const ys = ann.points.map(p => p.y);
      const minX = Math.min(...xs) - 10;
      const maxX = Math.max(...xs) + 10;
      const minY = Math.min(...ys) - 10;
      const maxY = Math.max(...ys) + 10;
      return point.x >= minX && point.x <= maxX && point.y >= minY && point.y <= maxY;
    }
    
    if (ann.type === 'text') {
      // For text annotations, estimate width based on content length and font size
      const fontSize = ann.style?.fontSize || 14;
      const content = ann.content || '';
      const lines = content.split('\n');
      const maxLineLength = Math.max(...lines.map(l => l.length));
      const estimatedWidth = maxLineLength * fontSize * 0.6; // Approximate character width
      const estimatedHeight = lines.length * fontSize * 1.2;
      
      const padding = 15;
      return (
        point.x >= ann.x - padding &&
        point.x <= ann.x + Math.max(estimatedWidth, 50) + padding &&
        point.y >= ann.y - fontSize - padding &&
        point.y <= ann.y + estimatedHeight + padding
      );
    }
    
    // For highlight and other types
    const padding = 5;
    return (
      point.x >= ann.x - padding &&
      point.x <= ann.x + (ann.width || 100) + padding &&
      point.y >= ann.y - padding &&
      point.y <= ann.y + (ann.height || 20) + padding
    );
  };
  
  // Add annotation
  const addAnnotation = (annotation) => {
    setAnnotations(prev => {
      const pageAnns = prev[currentPage] || [];
      return {
        ...prev,
        [currentPage]: [...pageAnns, annotation]
      };
    });
    setHasUnsavedChanges(true);
  };
  
  // Delete annotation
  const deleteAnnotation = (annotationId) => {
    setAnnotations(prev => {
      const pageAnns = prev[currentPage] || [];
      return {
        ...prev,
        [currentPage]: pageAnns.filter(a => a.id !== annotationId)
      };
    });
    setSelectedAnnotation(null);
    setHasUnsavedChanges(true);
  };
  
  // Add or update text annotation
  const addTextAnnotation = () => {
    if (!textInput.text.trim()) {
      setTextInput({ visible: false, x: 0, y: 0, text: '', editingId: null });
      return;
    }
    
    const now = new Date().toISOString();
    
    // If editing existing annotation
    if (textInput.editingId) {
      setAnnotations(prev => {
        const pageAnns = prev[currentPage] || [];
        const updatedAnns = pageAnns.map(ann => {
          if (ann.id === textInput.editingId) {
            return {
              ...ann,
              content: textInput.text,
              style: {
                ...ann.style,
                color: currentColor,
                fontSize: fontSize
              },
              updated_at: now
            };
          }
          return ann;
        });
        return {
          ...prev,
          [currentPage]: updatedAnns
        };
      });
      setTextInput({ visible: false, x: 0, y: 0, text: '', editingId: null });
      setHasUnsavedChanges(true);
      return;
    }
    
    // Create new annotation
    const newAnnotation = {
      id: `ann_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: 'text',
      page: currentPage,
      x: textInput.x,
      y: textInput.y,
      content: textInput.text,
      style: {
        color: currentColor,
        fontSize: fontSize,
        fontFamily: 'Arial',
        opacity: 1
      },
      created_at: now,
      updated_at: now
    };
    
    addAnnotation(newAnnotation);
    setTextInput({ visible: false, x: 0, y: 0, text: '', editingId: null });
  };
  
  // Clear all annotations on current page
  const clearPageAnnotations = () => {
    if (window.confirm(t('metanote.confirmClearPage') || 'Eliminare tutte le annotazioni di questa pagina?')) {
      setAnnotations(prev => ({
        ...prev,
        [currentPage]: []
      }));
      setHasUnsavedChanges(true);
    }
  };
  
  // Zoom controls
  const zoomIn = () => setScale(prev => Math.min(prev + 0.25, 3));
  const zoomOut = () => setScale(prev => Math.max(prev - 0.25, 0.5));
  
  // Page navigation
  const prevPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));
  const nextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('common.loading') || 'Caricamento...'}</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="flex flex-col h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white border-b px-4 py-3 flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              if (hasUnsavedChanges) {
                if (window.confirm(t('metanote.unsavedChanges') || 'Hai modifiche non salvate. Vuoi uscire senza salvare?')) {
                  navigate(-1);
                }
              } else {
                navigate(-1);
              }
            }}
            className="flex items-center gap-2"
          >
            <ArrowLeft size={20} />
            {t('common.back') || 'Indietro'}
          </Button>
          
          <div className="border-l pl-4">
            <h1 className="font-semibold text-lg">
              {t('metanote.title') || 'Meta Note'}
              {hasUnsavedChanges && <span className="text-red-500 ml-2">*</span>}
            </h1>
            <p className="text-sm text-gray-500 truncate max-w-md">
              {document?.original_filename}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          {lastSaved && (
            <span className="text-sm text-gray-500">
              {t('metanote.lastSaved') || 'Ultimo salvataggio'}: {lastSaved.toLocaleTimeString()}
            </span>
          )}
          
          <Button
            variant="outline"
            size="sm"
            onClick={saveAnnotations}
            disabled={saving}
            className="flex items-center gap-2"
          >
            <FloppyDisk size={18} />
            {saving ? (t('common.saving') || 'Salvataggio...') : (t('common.save') || 'Salva')}
          </Button>
        </div>
      </div>
      
      {/* Toolbar */}
      <div className="bg-white border-b px-4 py-2 flex items-center gap-6 shadow-sm">
        {/* Tool Selection */}
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
          <Button
            variant={currentTool === TOOLS.SELECT ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setCurrentTool(TOOLS.SELECT)}
            className="px-3"
            title={t('metanote.select') || 'Seleziona'}
          >
            <Circle size={18} />
          </Button>
          <Button
            variant={currentTool === TOOLS.TEXT ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setCurrentTool(TOOLS.TEXT)}
            className="px-3"
            title={t('metanote.text') || 'Testo'}
          >
            <TextT size={18} />
          </Button>
          <Button
            variant={currentTool === TOOLS.HIGHLIGHT ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setCurrentTool(TOOLS.HIGHLIGHT)}
            className="px-3"
            title={t('metanote.highlight') || 'Evidenziatore'}
          >
            <Highlighter size={18} />
          </Button>
          <Button
            variant={currentTool === TOOLS.FREEHAND ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setCurrentTool(TOOLS.FREEHAND)}
            className="px-3"
            title={t('metanote.freehand') || 'Mano libera'}
          >
            <PencilSimple size={18} />
          </Button>
          <Button
            variant={currentTool === TOOLS.ERASER ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setCurrentTool(TOOLS.ERASER)}
            className="px-3"
            title={t('metanote.eraser') || 'Gomma'}
          >
            <Eraser size={18} />
          </Button>
        </div>
        
        {/* Color Picker */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">{t('metanote.color') || 'Colore'}:</span>
          <div className="flex gap-1">
            {COLORS.map(color => (
              <button
                key={color}
                onClick={() => setCurrentColor(color)}
                className={`w-6 h-6 rounded-full border-2 transition-transform ${
                  currentColor === color ? 'border-blue-500 scale-110' : 'border-gray-300'
                }`}
                style={{ backgroundColor: color }}
                title={color}
              />
            ))}
          </div>
        </div>
        
        {/* Stroke Width */}
        {(currentTool === TOOLS.FREEHAND || currentTool === TOOLS.HIGHLIGHT) && (
          <div className="flex items-center gap-2 w-32">
            <span className="text-sm text-gray-600 whitespace-nowrap">{t('metanote.stroke') || 'Tratto'}:</span>
            <Slider
              value={[strokeWidth]}
              onValueChange={([v]) => setStrokeWidth(v)}
              min={1}
              max={20}
              step={1}
              className="flex-1"
            />
            <span className="text-sm w-6">{strokeWidth}</span>
          </div>
        )}
        
        {/* Font Size (for text) */}
        {currentTool === TOOLS.TEXT && (
          <div className="flex items-center gap-2 w-32">
            <span className="text-sm text-gray-600 whitespace-nowrap">{t('metanote.fontSize') || 'Dim.'}:</span>
            <Slider
              value={[fontSize]}
              onValueChange={([v]) => setFontSize(v)}
              min={8}
              max={48}
              step={1}
              className="flex-1"
            />
            <span className="text-sm w-6">{fontSize}</span>
          </div>
        )}
        
        {/* Opacity (for highlight) */}
        {currentTool === TOOLS.HIGHLIGHT && (
          <div className="flex items-center gap-2 w-32">
            <span className="text-sm text-gray-600 whitespace-nowrap">{t('metanote.opacity') || 'Opacità'}:</span>
            <Slider
              value={[opacity * 100]}
              onValueChange={([v]) => setOpacity(v / 100)}
              min={10}
              max={90}
              step={5}
              className="flex-1"
            />
            <span className="text-sm w-8">{Math.round(opacity * 100)}%</span>
          </div>
        )}
        
        <div className="flex-1" />
        
        {/* Delete selected / Clear page */}
        {selectedAnnotation && (
          <Button
            variant="destructive"
            size="sm"
            onClick={() => deleteAnnotation(selectedAnnotation)}
            className="flex items-center gap-2"
          >
            <Trash size={16} />
            {t('metanote.deleteSelected') || 'Elimina selezionato'}
          </Button>
        )}
        
        {/* Toggle annotations visibility */}
        <Button
          variant={showAnnotations ? "default" : "outline"}
          size="sm"
          onClick={() => setShowAnnotations(!showAnnotations)}
          className="flex items-center gap-2"
          title={showAnnotations ? (t('metanote.hideAnnotations') || 'Nascondi annotazioni') : (t('metanote.showAnnotations') || 'Mostra annotazioni')}
        >
          {showAnnotations ? <Eye size={16} /> : <EyeSlash size={16} />}
          {showAnnotations ? (t('metanote.hideAnnotations') || 'Nascondi') : (t('metanote.showAnnotations') || 'Mostra')}
        </Button>
        
        <Button
          variant="outline"
          size="sm"
          onClick={clearPageAnnotations}
          className="flex items-center gap-2 text-red-600 hover:text-red-700"
        >
          <Trash size={16} />
          {t('metanote.clearPage') || 'Cancella pagina'}
        </Button>
      </div>
      
      {/* Main Content Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* PDF Viewer */}
        <div 
          ref={containerRef}
          className="flex-1 overflow-auto bg-gray-200 p-4 flex justify-center"
        >
          <div className="relative inline-block shadow-lg">
            {/* PDF Canvas */}
            <canvas
              ref={canvasRef}
              className="block bg-white"
            />
            
            {/* Annotation Canvas (overlay) */}
            <canvas
              ref={annotationCanvasRef}
              className="absolute top-0 left-0"
              style={{ 
                cursor: currentTool === TOOLS.TEXT ? 'text' : 
                        currentTool === TOOLS.ERASER ? 'crosshair' :
                        currentTool === TOOLS.SELECT ? 'default' : 'crosshair'
              }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            />
            
            {/* Text Input Overlay */}
            {textInput.visible && (
              <div
                className="absolute bg-white border-2 border-blue-500 rounded shadow-lg p-2"
                style={{
                  left: textInput.x * scale,
                  top: textInput.y * scale - 30,
                  minWidth: 250,
                  maxWidth: 400
                }}
              >
                <div className="flex flex-col gap-2">
                  <textarea
                    autoFocus
                    value={textInput.text}
                    onChange={(e) => setTextInput(prev => ({ ...prev, text: e.target.value }))}
                    onKeyDown={(e) => {
                      // Only Escape closes without saving
                      if (e.key === 'Escape') {
                        setTextInput({ visible: false, x: 0, y: 0, text: '', editingId: null });
                      }
                      // Enter creates new line (default behavior for textarea)
                      // No special handling needed - textarea handles multiline by default
                    }}
                    className="w-full min-h-[60px] max-h-[200px] p-2 text-sm border rounded resize-y focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={t('metanote.enterText') || 'Inserisci testo...'}
                    style={{ 
                      color: currentColor,
                      fontSize: fontSize 
                    }}
                  />
                  <div className="flex items-center justify-end gap-1">
                    <span className="text-xs text-gray-500 mr-2">
                      {textInput.editingId ? (t('metanote.editing') || 'Modifica') : (t('metanote.new') || 'Nuova nota')}
                    </span>
                    <Button size="sm" variant="default" onClick={addTextAnnotation} className="h-8 px-3 bg-green-600 hover:bg-green-700">
                      <Check size={16} className="mr-1" />
                      {t('common.confirm') || 'Conferma'}
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => setTextInput({ visible: false, x: 0, y: 0, text: '', editingId: null })}
                      className="h-8 px-3"
                    >
                      <X size={16} className="mr-1" />
                      {t('common.cancel') || 'Annulla'}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Footer - Page Navigation & Zoom */}
      <div className="bg-white border-t px-4 py-2 flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={zoomOut}
            disabled={scale <= 0.5}
          >
            <MagnifyingGlassMinus size={18} />
          </Button>
          <span className="text-sm min-w-16 text-center">{Math.round(scale * 100)}%</span>
          <Button
            variant="outline"
            size="sm"
            onClick={zoomIn}
            disabled={scale >= 3}
          >
            <MagnifyingGlassPlus size={18} />
          </Button>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={prevPage}
            disabled={currentPage <= 1}
          >
            <CaretLeft size={18} />
          </Button>
          <span className="text-sm min-w-24 text-center">
            {t('metanote.page') || 'Pagina'} {currentPage} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={nextPage}
            disabled={currentPage >= totalPages}
          >
            <CaretRight size={18} />
          </Button>
        </div>
        
        <div className="text-sm text-gray-500">
          {(annotations[currentPage]?.length || 0)} {t('metanote.annotationsOnPage') || 'annotazioni su questa pagina'}
        </div>
      </div>
    </div>
  );
};

export default MetaNotePage;
